import React from 'react';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import PropTypes from 'prop-types';
import CustomDialog from '@/components/common/CustomDialog';
import InteractionDialog from '@/components/common/interaction/InteractionDialog';
import MultiActionDialogKey from '@/constants/MultiActionDialogKey';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useMobileView from '@/hooks/ui/useMobileView';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import translate from '@/lib/intl/translate';
import ConstituentPropType from '@/lib/types/ConstituentPropType';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { ConstituentCardItemActionsAssign } from '@/modules/ConstituentDetails';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import getUpdatedBoardPosition from '../lib/getUpdatedBoardPosition';
import useLifecycleBoard from '../lib/hooks/useLifecycleBoard';
import { DONOR_ACTION_CONFIG } from '../lib/LifecycleBoardConfig';
import { useLifecycleBoardContext } from './LifecycleBoardContext';

function MenuItem({ children, constituent, onClick, shouldOpenDonor }) {
  const { constituentId } = constituent;
  const props = { className: 'flex items-center p-4 w-full' };
  let Component = 'button';
  if (shouldOpenDonor) {
    Component = RouterLink;
    props.to = generatePath(AuthRoutes.CONSTITUENT_EDIT, {
      constituentId,
    });
  } else {
    props.onClick = onClick;
    props.type = 'button';
  }
  return <Component {...props}>{children}</Component>;
}

MenuItem.propTypes = {
  children: PropTypes.node.isRequired,
  constituent: ConstituentPropType.isRequired,
  onClick: PropTypes.func,
  shouldOpenDonor: PropTypes.bool,
};

MenuItem.defaultProps = {
  onClick: () => {},
  shouldOpenDonor: false,
};

const FollowUpAction = ({ action, onClose, constituent }) => {
  let content;
  const { constituentId, displayName } = constituent;

  switch (action) {
    case MultiActionDialogKey.INTERACTION:
      content = (
        <InteractionDialog
          onClose={onClose}
          preSelectedDonorId={constituentId}
        />
      );
      break;
    case MultiActionDialogKey.ASSIGN:
      content = (
        <ConstituentCardItemActionsAssign
          constituent={constituent}
          header={<div className="px-4 pt-4 type-h2">{displayName}</div>}
          onClose={onClose}
        />
      );
      break;
    default:
      content = null;
      break;
  }

  return content;
};

function LifecycleCardMenu({
  boardId,
  columnId,
  constituentId,
  onClose,
  open,
  target,
}) {
  const [selectedAction, setSelectedAction] = React.useState(null);
  const isMobile = useMobileView();
  const { data: constituent } = useConstituent(constituentId);
  const { moveConstituent, removeConstituent } = useLifecycleBoardContext();
  const { removeConstituent: removeConstituentPosition } =
    useLifecycleBoard(boardId);

  const closeAction = () => {
    setSelectedAction(null);
    onClose();
  };

  const { closeDialog, openDialog } = useDialogManager();

  React.useEffect(() => {
    switch (selectedAction) {
      case MultiActionDialogKey.DONATION:
        openDialog(Dialogs.DONATION_FORM, {
          onClose: closeAction,
          preSelectedDonorId: constituentId,
        });
        break;
      case MultiActionDialogKey.MOVE:
        openDialog(Dialogs.CONSTITUENT_LIFECYCLE_POSITIONS, {
          constituentId,
          onClose: closeAction,
          onUpdate: (newBoards) => {
            const updatedBoardPosition = newBoards.find(
              (board) => board.boardId === boardId,
            );
            if (updatedBoardPosition) {
              const newConstituent = getUpdatedBoardPosition(
                constituent,
                updatedBoardPosition,
              );
              moveConstituent(
                newConstituent,
                columnId,
                updatedBoardPosition.columnId,
              );
            } else {
              removeConstituent(constituent, columnId);
            }
          },
        });

        break;
      case MultiActionDialogKey.REMOVE:
        openDialog(Dialogs.CONFIRMATION, {
          children: translate('CONSTITUENT_REMOVE_CONFIRMATION'),
          confirmLabel: translate('REMOVE'),
          onCancel: closeAction,
          onClose: closeAction,
          onConfirm: () => {
            removeConstituent(constituent, columnId);
            removeConstituentPosition(constituentId);
            closeAction();
          },
        });
        break;
      default:
        closeDialog();
    }
  }, [selectedAction]);

  if (!constituent) return null;

  const Wrapper = isMobile ? CustomDialog : Popover;
  const wrapperProps = isMobile
    ? {
        fullWidth: true,
        maxWidth: 'md',
        mobile: true,
      }
    : {
        anchorEl: target,
        anchorOrigin: { vertical: 'top', horizontal: 'left' },
      };

  return (
    <>
      <Wrapper open={open} onClose={onClose} {...wrapperProps}>
        <div className="max-w-full bg-white md:w-96">
          <div className="flex items-center type-body2">
            <div className="mx-4">{translate('DONOR_BOARD_ACTIONS')}</div>
            <IconButton onClick={onClose} className="ml-auto" size="large">
              <Close />
            </IconButton>
          </div>
          <ul>
            {DONOR_ACTION_CONFIG.map(({ key, icon, text, shouldOpenDonor }) => (
              <li
                key={key}
                className="border-t border-grey-300 hover:bg-grey-100"
              >
                <MenuItem
                  constituent={constituent}
                  onClick={() => setSelectedAction(key)}
                  shouldOpenDonor={shouldOpenDonor}
                >
                  <img className="mr-3 h-5" alt="" src={icon} />
                  <span
                    className={`type-body1 ${
                      key === MultiActionDialogKey.REMOVE
                        ? 'text-error-main'
                        : ''
                    }`}
                  >
                    {text}
                  </span>
                </MenuItem>
              </li>
            ))}
          </ul>
        </div>
      </Wrapper>

      {selectedAction &&
        ![MultiActionDialogKey.DONATION, MultiActionDialogKey.MOVE].includes(
          selectedAction,
        ) && (
          <Dialog
            fullWidth
            maxWidth="md"
            open={selectedAction}
            onClose={closeAction}
            scroll="paper"
            showDialogActions={false}
          >
            <FollowUpAction
              action={selectedAction}
              onClose={closeAction}
              constituent={constituent}
            />
          </Dialog>
        )}
    </>
  );
}

LifecycleCardMenu.propTypes = {
  boardId: PropTypes.string,
  columnId: PropTypes.string,
  constituentId: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  target: PropTypes.element,
};

LifecycleCardMenu.defaultProps = {
  boardId: null,
  columnId: null,
  constituentId: null,
  onClose: () => {},
  open: false,
  target: null,
};

export default LifecycleCardMenu;
